import React, { createContext, useContext, useEffect, useState } from 'react';
import API_URL from '../config/api';

// Decode a JWT token payload without a library.
// Only reads the public payload — no verification (verification is done server-side).
function decodeTokenPayload(token: string): Record<string, any> | null {
  try {
    const payload = token.split('.')[1];
    if (!payload) return null;
    // Pad base64url to standard base64
    const padded = payload.replace(/-/g, '+').replace(/_/g, '/').padEnd(
      payload.length + (4 - (payload.length % 4)) % 4, '='
    );
    return JSON.parse(atob(padded));
  } catch {
    return null;
  }
}

type AuthContextType = {
  token: string | null;
  role: string | null;
  business_id: string | null;
  userName: string | null;
  userEmail: string | null;
  plan: string | null; // 'starter' | 'pro' | 'enterprise'
  business_type: string | null; // e.g. 'Hair and Beauty', 'Property & Lettings'
  login: (token: string, role?: string | null, business_id?: string | null, business_type?: string | null) => void;
  logout: () => void;
};

const AuthContext = createContext<AuthContextType | undefined>(undefined);

// Extract display name and email from a stored token
function getUserInfoFromToken(token: string | null): { userName: string | null; userEmail: string | null } {
  if (!token) return { userName: null, userEmail: null };
  const payload = decodeTokenPayload(token);
  if (!payload) return { userName: null, userEmail: null };
  const email: string | null = payload.email ?? null;
  const name: string | null = payload.name ?? null;
  // Prefer name; fall back to email
  const userName = name || email;
  return { userName, userEmail: email };
}

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [token, setToken] = useState<string | null>(() => localStorage.getItem('token'));
  const [role, setRole] = useState<string | null>(() => localStorage.getItem('role'));
  const [business_id, setBusinessId] = useState<string | null>(() => localStorage.getItem('business_id'));
  const [plan, setPlan] = useState<string | null>(() => localStorage.getItem('plan'));
  const [business_type, setBusinessType] = useState<string | null>(() => localStorage.getItem('business_type'));
  const [userName, setUserName] = useState<string | null>(() => {
    const { userName } = getUserInfoFromToken(localStorage.getItem('token'));
    return userName;
  });
  const [userEmail, setUserEmail] = useState<string | null>(() => {
    const { userEmail } = getUserInfoFromToken(localStorage.getItem('token'));
    return userEmail;
  });

  useEffect(() => {
    // sync localStorage -> state when other tabs change
    const onStorage = () => {
      const storedToken = localStorage.getItem('token');
      const { userName: n, userEmail: e } = getUserInfoFromToken(storedToken);
      setToken(storedToken);
      setRole(localStorage.getItem('role'));
      setBusinessId(localStorage.getItem('business_id'));
      setPlan(localStorage.getItem('plan'));
      setBusinessType(localStorage.getItem('business_type'));
      setUserName(n);
      setUserEmail(e);
    };
    window.addEventListener('storage', onStorage);
    return () => window.removeEventListener('storage', onStorage);
  }, []);

  // Fetch latest role + plan from server whenever token changes (including after login)
  useEffect(() => {
    const storedToken = localStorage.getItem('token');
    if (!storedToken) return;
    fetch(`${API_URL}/auth/me`, { headers: { Authorization: `Bearer ${storedToken}` } })
      .then(r => r.ok ? r.json() : null)
      .then(data => {
        if (data?.role) {
          localStorage.setItem('role', data.role);
          setRole(data.role);
        }
        if (data?.plan) {
          localStorage.setItem('plan', data.plan);
          setPlan(data.plan);
        }
        if (data?.business_type !== undefined) {
          localStorage.setItem('business_type', data.business_type || '');
          setBusinessType(data.business_type || null);
        }
      })
      .catch(() => { /* silently ignore network errors */ });
  }, [token]);

  const login = (t: string, r: string | null = null, b: string | null = null, bt: string | null = null) => {
    localStorage.setItem('token', t);
    if (r) localStorage.setItem('role', r);
    if (b) localStorage.setItem('business_id', b);
    if (bt !== null) { localStorage.setItem('business_type', bt); setBusinessType(bt); }
    const { userName: n, userEmail: e } = getUserInfoFromToken(t);
    setToken(t);
    setRole(r);
    setBusinessId(b);
    setUserName(n);
    setUserEmail(e);
  };

  const logout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('role');
    localStorage.removeItem('business_id');
    localStorage.removeItem('plan');
    localStorage.removeItem('business_type');
    setToken(null);
    setRole(null);
    setBusinessId(null);
    setPlan(null);
    setBusinessType(null);
    setUserName(null);
    setUserEmail(null);
  };

  return (
    <AuthContext.Provider value={{ token, role, business_id, userName, userEmail, plan, business_type, login, logout }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = (): AuthContextType => {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
};

export default AuthContext;
